/** Helpers for new-user free trial access (default 30 days). */

export const FREE_TRIAL_DAYS = 30;
export const BETA_TRIAL_DAYS = 60;

/** Active beta program participant (not expired). */
export function isActiveBetaUser(userData) {
  return Boolean(
    userData &&
    userData.is_beta &&
    userData.beta_expired !== true
  );
}

/** Days between subscription start and end (inclusive-friendly). */
export function subscriptionDurationDays(subscription) {
  if (!subscription?.start_date || !subscription?.end_date) return null;
  try {
    const start = new Date(subscription.start_date);
    const end = new Date(subscription.end_date);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    const days = Math.round((end - start) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : null;
  } catch (_) {
    return null;
  }
}

export function isSubscriptionActive(subscription) {
  if (!subscription) return false;

  const status = String(subscription.status || '').toLowerCase();
  if (status === 'cancelled' || status === 'canceled' || status === 'expired') {
    return false;
  }

  const endDateRaw = subscription.end_date;
  if (!endDateRaw) {
    return status === 'active';
  }

  const endDate = new Date(endDateRaw);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return endDate >= today;
}

export function hasActiveSubscription(userData) {
  return isSubscriptionActive(userData?.subscription);
}

export function getApiBase() {
  return (process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai/api').replace(/\/api$/, '');
}

export async function fetchActiveSubscriptionFromApi(token) {
  if (!token) return null;

  try {
    const res = await fetch(`${getApiBase()}/api/addons/my-plan`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;

    const body = await res.json().catch(() => ({}));
    const data = body?.data || body;
    if (!data?.end_date && !data?.plan_id) return null;

    return {
      status: 'active',
      end_date: data.end_date,
      start_date: data.start_date,
      plan_id: data.plan_id,
      plan_interval: data.plan_interval,
      is_free_trial: data.is_free_trial,
      is_beta_complimentary: data.is_beta_complimentary,
      is_gift_subscription: data.is_gift_subscription,
      trial_days: data.trial_days,
      underlying_plan_interval: data.underlying_plan_interval,
      auto_renew: data.auto_renew,
      plan: {
        ...(data.plan_details || {}),
        name: data.plan_name || data.plan_details?.name,
        price_usd: data.plan_details?.price_usd,
        avatar_type_id: data.plan_details?.avatar_type_id,
      },
    };
  } catch (_) {
    return null;
  }
}

export function hasOwnedAvatars(avatars) {
  return Array.isArray(avatars) && avatars.length > 0;
}

/** Read cached avatar list from sessionStorage (set after sign-in). */
export function getCachedAvatars() {
  try {
    const raw = sessionStorage.getItem('avatars_data');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

export function userHasAvatars(avatars) {
  return hasOwnedAvatars(avatars) || hasOwnedAvatars(getCachedAvatars());
}

export function isGiftInvitee(userData = null) {
  if (String(userData?.invitee?.i_will_pay ?? '') === '1') return true;
  try {
    const stored = JSON.parse(localStorage.getItem('signup_invite_params') || '{}');
    if (String(stored.i_will_pay) === '1') return true;
    const cached = JSON.parse(sessionStorage.getItem('user_data') || '{}');
    if (String(cached?.invitee?.i_will_pay ?? '') === '1') return true;
  } catch (_) { /* ignore */ }
  return false;
}

/** Family send-invite invitee (i_will_pay=0) — group chat only until they upgrade. */
export function isSendInviteInvitee(userData = null, inviteId = null, iWillPay = null) {
  if (isGiftInvitee(userData)) return false;
  if (!isKnownInviteeUser(userData, inviteId, iWillPay)) return false;
  const ctx = resolveInviteContext({ inviteId, iWillPay, userData });
  if (ctx.iWillPay == null || ctx.iWillPay === '') return true;
  return String(ctx.iWillPay) !== '1';
}

/** True when a send-invite user has upgraded to a paid personal plan (not free trial / gift). */
export function hasPaidPersonalSubscription(userData = null) {
  const sub = userData?.subscription;
  if (!sub || !isSubscriptionActive(sub)) return false;
  if (sub.is_gift_subscription === true) return false;
  if (isFreeTrialSubscription(sub, userData)) return false;
  return true;
}

/**
 * Send-invite invitee of an active beta inviter — complimentary access during beta.
 */
export function isBetaInvitedFreeAccess(userData = null, inviteId = null, iWillPay = null) {
  if (!isSendInviteInvitee(userData, inviteId, iWillPay)) return false;
  if (isActiveBetaUser(userData)) return true;
  if (Boolean(userData?.subscription?.is_beta_complimentary)) return true;
  if (String(userData?.invitee?.inviter_is_beta) === 'true' || userData?.invitee?.inviter_is_beta === true) {
    return true;
  }
  return false;
}

/**
 * Send-invite invitee still on limited group-only access (no paid upgrade yet).
 * Beta inviter invitees with complimentary trial are not limited.
 */
export function isLimitedSendInviteInvitee(userData = null, inviteId = null, iWillPay = null) {
  if (!isSendInviteInvitee(userData, inviteId, iWillPay)) return false;
  if (hasActiveSubscription(userData)) return false;
  if (isBetaInvitedFreeAccess(userData, inviteId, iWillPay)) return false;
  return true;
}

/** Show invited-member billing UI for all send-invite users (including after they pay). */
export function shouldUseLimitedInviteePlanDisplay(userData = null) {
  return isSendInviteInvitee(userData);
}

/** Pay-to-join group chat plans — only for send-invite users who have not paid yet. */
export function shouldShowGroupChatUpgradePlans(userData = null) {
  return isLimitedSendInviteInvitee(userData);
}

/**
 * Send-invite user (i_will_pay=0) who must pay before accessing chat.
 */
export function shouldRouteUnpaidSendInviteToPayment(userData = null, inviteId = null, iWillPay = null) {
  return isLimitedSendInviteInvitee(userData, inviteId, iWillPay);
}

/** Invited user may enter chat — gift invitee or send-invite who has paid. */
export function canAccessInviteeChat(userData = null, inviteId = null, iWillPay = null) {
  if (!isKnownInviteeUser(userData, inviteId, iWillPay)) return false;
  return !isLimitedSendInviteInvitee(userData, inviteId, iWillPay);
}

/** Invited family members join an existing avatar — they never create their own. */
export function canUserCreateAvatar(userData = null, inviteId = null, iWillPay = null) {
  return !isKnownInviteeUser(userData, inviteId, iWillPay);
}

/** Merge /addons/my-plan onto user when session user is stale (e.g. right after PayPal capture). */
export async function mergeUserWithLatestSubscription(userData, token = null) {
  if (!userData) return userData;
  const apiSub = await fetchActiveSubscriptionFromApi(token || localStorage.getItem('access_token'));
  if (isSubscriptionActive(apiSub)) {
    return { ...userData, subscription: apiSub };
  }
  if (apiSub && !userData.subscription) {
    return { ...userData, subscription: apiSub };
  }
  return userData;
}

export function getLimitedInviteePlanDisplay() {
  return {
    planName: 'Group Chat',
    planInterval: 'group',
    planIntervalLabel: 'Invited member',
    planPrice: null,
    planPriceLabel: 'Limited access',
    isFreeTrial: false,
    hasSubscription: false,
    isLimitedGroupAccess: true,
    isGiftSubscription: false,
  };
}

/** Paid upgrade options for send-invite users (prefer avatar types named "Group"). */
export function filterGroupChatUpgradePlans(avatarPlans = []) {
  if (!Array.isArray(avatarPlans) || avatarPlans.length === 0) return [];
  const groupTypes = avatarPlans.filter((t) => /group/i.test(String(t.name || '')));
  if (groupTypes.length > 0) return groupTypes;
  return avatarPlans.filter((t) => {
    const plans = t.plans || [];
    return plans.some(
      (p) => Number(p.monthly_price_usd) > 0 || Number(p.yearly_price_usd) > 0
    );
  });
}

/** Gift plan label/price from invitee.plan_pricing_id + avatar-plans catalog. */
export function resolveGiftPlanDisplay(userData, avatarPlans = []) {
  if (!isGiftInvitee(userData)) return null;

  let ppid = Number(userData?.invitee?.plan_pricing_id);
  if (!Number.isFinite(ppid) || ppid <= 0) {
    try {
      const stored = JSON.parse(localStorage.getItem('signup_invite_params') || '{}');
      ppid = Number(stored.plan_pricing_id);
    } catch (_) { /* ignore */ }
  }
  if (Number.isFinite(ppid) && ppid > 0 && Array.isArray(avatarPlans)) {
    for (const type of avatarPlans) {
      for (const plan of type.plans || []) {
        if (Number(plan.id) !== ppid) continue;
        const isYearly = Number(plan.yearly_price_usd) > 0;
        const isMonthly = Number(plan.monthly_price_usd) > 0;
        const interval = isYearly ? 'yearly' : (isMonthly ? 'monthly' : 'monthly');
        const price = isYearly
          ? `$${plan.yearly_price_usd}/year`
          : isMonthly
            ? `$${plan.monthly_price_usd}/month`
            : '$19.99/month';
        return {
          planName: isYearly ? 'Annual' : 'Monthly',
          planInterval: interval,
          planIntervalLabel: isYearly ? 'Annual' : 'Monthly',
          planPrice: isYearly ? `$${plan.yearly_price_usd}` : `$${plan.monthly_price_usd}`,
          planPriceLabel: isYearly ? `$${plan.yearly_price_usd}` : `$${plan.monthly_price_usd}`,
          isFreeTrial: false,
          hasSubscription: true,
          isGiftSubscription: true,
          trialDays: null,
        };
      }
    }
  }

  return {
    planName: 'Monthly',
    planInterval: 'monthly',
    planIntervalLabel: 'Monthly',
    planPrice: '$19.99',
    planPriceLabel: '$19.99',
    isFreeTrial: false,
    hasSubscription: true,
    isGiftSubscription: true,
    trialDays: null,
  };
}

export function isFreeTrialSubscription(subscription, userData = null) {
  if (!subscription) return false;
  if (isGiftInvitee(userData) || subscription.is_gift_subscription === true) return false;
  const isBetaUser =
    userData &&
    Boolean(userData.is_beta) &&
    userData.beta_expired !== true;
  if (!isBetaUser) return false;
  if (subscription.is_free_trial === true) return true;
  if (!isSubscriptionActive(subscription)) return false;
  if (String(subscription.plan_interval || '').toLowerCase() === 'free_trial') return true;
  const orderId = String(subscription.paypal_order_id || '').toUpperCase();
  return orderId.startsWith('FREE-TRIAL-') || orderId.startsWith('FREE-');
}

/** Normalized plan label/price for profile & billing UI. */
export function getPlanDisplayInfo(userData, avatarPlans = null) {
  const subscription = userData?.subscription;
  if (!subscription) {
    const giftDisplay = resolveGiftPlanDisplay(userData, avatarPlans || []);
    if (giftDisplay) return giftDisplay;
    return {
      planName: null,
      planInterval: null,
      planPrice: null,
      isFreeTrial: false,
      hasSubscription: false,
    };
  }

  const planDetails = subscription.plan || {};
  const isFreeTrial = isFreeTrialSubscription(subscription, userData);

  if (isFreeTrial) {
    const fromDates = subscriptionDurationDays(subscription);
    const trialDays = userData?.is_beta
      ? (fromDates || subscription.trial_days || BETA_TRIAL_DAYS)
      : (fromDates || subscription.trial_days || planDetails.plan_duration_days || FREE_TRIAL_DAYS);
    if (isActiveBetaUser(userData)) {
      return {
        planName: 'Free Trial',
        planInterval: 'free_trial',
        planIntervalLabel: 'Free Trial',
        planPrice: '$0',
        planPriceLabel: `$0 for ${trialDays} days`,
        isFreeTrial: true,
        hasSubscription: true,
        trialDays,
      };
    }
    const underlying =
      subscription.underlying_plan_interval ||
      planDetails.plan_interval ||
      null;
    const planLabel =
      planDetails.name ||
      (underlying === 'monthly' ? 'Monthly' : underlying === 'yearly' ? 'Annual' : 'Free Trial');
    return {
      planName: planLabel,
      planInterval: 'free_trial',
      planIntervalLabel: 'Free Trial',
      planPrice: '$0',
      planPriceLabel: `$0 for ${trialDays} days`,
      isFreeTrial: true,
      hasSubscription: true,
      trialDays,
    };
  }

  const planName = planDetails.name || subscription.plan_name || null;
  const planInterval =
    subscription.underlying_plan_interval ||
    subscription.plan_interval ||
    planDetails.plan_interval ||
    planDetails.interval ||
    null;
  let planPrice = null;
  if (planDetails.price_usd !== undefined && planDetails.price_usd !== null) {
    planPrice = `$${planDetails.price_usd}`;
  } else if (planInterval === 'yearly' || planInterval === 'year') {
    planPrice = '$149.99/year';
  } else if (planInterval === 'monthly') {
    planPrice = '$19.99/month';
  }

  return {
    planName: planName || 'Standard Plan',
    planInterval,
    planPrice,
    planPriceLabel: planPrice,
    isFreeTrial: false,
    hasSubscription: true,
    isGiftSubscription: Boolean(subscription.is_gift_subscription || isGiftInvitee(userData)),
    trialDays: null,
  };
}

export function getPlanNameFromUser(userData, userId = null) {
  const info = getPlanDisplayInfo(userData);
  if (info.planName) return info.planName;

  if (userId) {
    try {
      const key = `activated_plan_for_user_${userId}`;
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (String(parsed?.plan_interval || '').toLowerCase() === 'free_trial') return 'Free Trial';
      }
    } catch (_) { /* ignore */ }
  }

  return null;
}

/** True when a paid plan card should show as the user's active subscription. */
export function isPaidPlanCardCurrent({
  isFreeTrial = false,
  userPlanId = null,
  planId = null,
  userInterval = '',
  billingPeriod = '',
} = {}) {
  if (isFreeTrial) return false;
  const uid = Number(userPlanId);
  const pid = Number(planId);
  if (!Number.isFinite(uid) || !Number.isFinite(pid) || uid !== pid) return false;
  const interval = String(userInterval || '').toLowerCase();
  // Avatar plan rows are one interval per id (monthly row vs yearly row).
  if (!interval || interval === 'free_trial') return true;
  return interval === String(billingPeriod || '').toLowerCase();
}

export function resolveUserPlanInterval(userData, avatarPlans = null) {
  const sub = userData?.subscription;
  const fromSub = String(
    sub?.underlying_plan_interval ||
    sub?.plan_interval ||
    sub?.plan?.plan_interval ||
    ''
  ).toLowerCase();
  if (fromSub && fromSub !== 'free_trial') return fromSub;

  const pid = resolveUserPlanId(userData);
  if (!Number.isFinite(pid) || pid <= 0 || !Array.isArray(avatarPlans)) return '';

  for (const type of avatarPlans) {
    for (const plan of type.plans || []) {
      if (Number(plan.id) !== pid) continue;
      if (Number(plan.yearly_price_usd) > 0) return 'yearly';
      if (Number(plan.monthly_price_usd) > 0) return 'monthly';
      const planInterval = String(plan.interval || '').toLowerCase();
      if (planInterval === 'yearly' || planInterval === 'year') return 'yearly';
      if (planInterval === 'monthly') return 'monthly';
    }
  }
  return 'monthly';
}

export function resolveUserPlanId(userData) {
  const sub = userData?.subscription;
  if (sub?.plan_id != null) return Number(sub.plan_id);
  if (userData?.invitee?.plan_pricing_id != null) return Number(userData.invitee.plan_pricing_id);
  try {
    const stored = JSON.parse(localStorage.getItem('signup_invite_params') || '{}');
    if (stored.plan_pricing_id != null) return Number(stored.plan_pricing_id);
  } catch (_) { /* ignore */ }
  return null;
}

export function extractAvatarsList(response) {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.avatars)) return response.avatars;

  const data = response?.data ?? response;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.avatars)) return data.avatars;
  if (data?.avatar && typeof data.avatar === 'object') return [data.avatar];
  return [];
}

export function getStoredInviteParams() {
  try {
    const raw = localStorage.getItem('signup_invite_params');
    return raw ? JSON.parse(raw) : {};
  } catch (_) {
    return {};
  }
}

/** Merge invite context from URL, localStorage, and GET /me invitee payload. */
export function resolveInviteContext({
  inviteId = null,
  iWillPay = null,
  userData = null,
  fromInviteLink = false,
} = {}) {
  const stored = getStoredInviteParams();
  const invitee = userData?.invitee || null;
  const resolvedInviteId = inviteId ?? stored.invite_id ?? invitee?.invite_id ?? null;
  const resolvedIWillPay = iWillPay ?? stored.i_will_pay ?? invitee?.i_will_pay ?? null;
  return {
    inviteId: resolvedInviteId,
    iWillPay: resolvedIWillPay,
    avatarId: stored.avatar_id ?? invitee?.avatar_id ?? null,
    isGiftInvitee: String(resolvedIWillPay) === '1',
    isKnownInvitee: Boolean(resolvedInviteId || invitee?.invite_id),
    fromInviteLink: fromInviteLink || Boolean(stored.invite_id || invitee?.invite_id),
  };
}

/** Persist invitee context from GET /me so relation routing works after logout/login. */
export function syncSignupInviteParamsFromMe(userData, urlAvatarId = null) {
  try {
    let stored = {};
    try {
      stored = JSON.parse(localStorage.getItem('signup_invite_params') || '{}');
    } catch (_) {
      stored = {};
    }
    const invitee = userData?.invitee;
    let changed = false;
    if (invitee) {
      if (invitee.invite_id != null) {
        stored.invite_id = invitee.invite_id;
        changed = true;
      }
      if (invitee.invite_email) {
        stored.invite_email = invitee.invite_email;
        changed = true;
      }
      if (invitee.i_will_pay != null) {
        stored.i_will_pay = invitee.i_will_pay;
        changed = true;
      }
      if (invitee.avatar_id != null) {
        stored.avatar_id = invitee.avatar_id;
        changed = true;
      }
      if (invitee.inviter_is_beta != null) {
        stored.inviter_is_beta = invitee.inviter_is_beta;
        changed = true;
      }
    }
    if (urlAvatarId) {
      stored.avatar_id = urlAvatarId;
      changed = true;
    }
    if (changed) {
      localStorage.setItem('signup_invite_params', JSON.stringify(stored));
    }
  } catch (_) {
    /* ignore */
  }
}

/** Pick a shared (invited) avatar id — never use owned avatars for relation capture. */
export function pickAvatarIdFromAvatars(avatars = []) {
  if (!Array.isArray(avatars) || avatars.length === 0) return null;
  const shared = avatars.find((a) => a?.is_owner === false);
  if (!shared) return null;
  const n = parseInt(shared?.id ?? shared?.avatar_id, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Clear cached avatar/session flags when switching accounts (signup/signin). */
export function clearStaleAuthSessionCaches() {
  try {
    sessionStorage.removeItem('avatars_data');
    sessionStorage.removeItem('has_avatars');
    sessionStorage.removeItem('avatars_fetched');
    sessionStorage.removeItem('user_fetched');
  } catch (_) { /* ignore */ }
}

/** Unwrap member-relation API payload ({ status, data: { needs_capture, ... } }). */
export function unwrapMemberRelationPayload(response) {
  const outer = response?.data ?? response;
  if (
    outer &&
    typeof outer === 'object' &&
    (outer.needs_capture !== undefined || outer.relation !== undefined || outer.avatar_name !== undefined)
  ) {
    return outer;
  }
  if (outer?.data && typeof outer.data === 'object') {
    return outer.data;
  }
  return outer && typeof outer === 'object' ? outer : {};
}

/** Best avatar id for relation-capture (URL → localStorage → /me → shared avatars). */
export function resolveInviteAvatarIdForRelation({
  avatarId = null,
  userData = null,
  avatars = [],
} = {}) {
  const ctx = resolveInviteContext({ userData });
  const candidates = [
    avatarId,
    ctx.avatarId,
    userData?.invitee?.avatar_id,
  ];
  for (const c of candidates) {
    const n = parseInt(c, 10);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return pickAvatarIdFromAvatars(avatars);
}

/** Resolve invite avatar id, fetching my-details when the list is empty (family invitees). */
export async function resolveInviteAvatarIdForRelationAsync({
  avatarId = null,
  userData = null,
  avatars = [],
  userId = null,
  avatarsAPI = null,
  inviteId = null,
  iWillPay = null,
  maxRetries = 5,
} = {}) {
  let n = resolveInviteAvatarIdForRelation({ avatarId, userData, avatars });
  if (n || !avatarsAPI) return n;

  const knownInvitee = isKnownInviteeUser(userData, inviteId, iWillPay);
  if (!knownInvitee) return null;

  for (let i = 0; i < maxRetries; i++) {
    if (i > 0) {
      await new Promise((resolve) => setTimeout(resolve, 800));
    }
    try {
      const details = await avatarsAPI.getMyDetails();
      const list = extractAvatarsList(details);
      n = resolveInviteAvatarIdForRelation({ avatarId, userData, avatars: list });
      if (n) return n;
    } catch (_) { /* ignore */ }
  }
  return n;
}

/** Relation-capture path for invitees, with hard fallback when API is slow right after signup. */
export async function resolveInviteePostAuthRoute({
  avatars = [],
  inviteId = null,
  iWillPay = null,
  userData = null,
  userId = null,
  avatarId = null,
  avatarsAPI = null,
} = {}) {
  const ctx = resolveInviteContext({ inviteId, iWillPay, userData });
  if (!ctx.isKnownInvitee) return null;

  if (shouldRouteUnpaidSendInviteToPayment(userData, inviteId, iWillPay)) {
    const apiSub = await fetchActiveSubscriptionFromApi(
      typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null
    );
    if (!isSubscriptionActive(apiSub)) {
      return '/select-plan';
    }
  }

  const relationRoute = await resolveRelationCaptureRoute({
    avatarId,
    userId,
    avatars,
    avatarsAPI,
    userData,
    inviteId,
    iWillPay,
  });
  if (relationRoute) return relationRoute;

  const fallbackCandidates = [
    avatarId,
    ctx.avatarId,
    userData?.invitee?.avatar_id,
    pickAvatarIdFromAvatars(avatars),
  ];
  for (const c of fallbackCandidates) {
    const n = parseInt(c, 10);
    if (!Number.isFinite(n) || n <= 0) continue;
    if (userId && isRelationCaptured(userId, n)) continue;
    return `/relation-capture?avatar_id=${n}`;
  }

  return '/chat';
}

/** True when this account joined via an invitation (gift or family send_invite). */
export function isKnownInviteeUser(userData = null, inviteId = null, iWillPay = null) {
  return resolveInviteContext({ inviteId, iWillPay, userData }).isKnownInvitee;
}

const RELATION_CAPTURED_KEY = 'sayagain_relation_captured';

export function markRelationCaptured(userId, avatarId) {
  if (!userId || !avatarId) return;
  try {
    const raw = localStorage.getItem(RELATION_CAPTURED_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[`${userId}:${avatarId}`] = true;
    localStorage.setItem(RELATION_CAPTURED_KEY, JSON.stringify(map));
  } catch (_) { /* ignore */ }
}

export function isRelationCaptured(userId, avatarId) {
  if (!userId || !avatarId) return false;
  try {
    const raw = localStorage.getItem(RELATION_CAPTURED_KEY);
    const map = raw ? JSON.parse(raw) : {};
    return Boolean(map[`${userId}:${avatarId}`]);
  } catch (_) {
    return false;
  }
}

/**
 * Invited users always use chat with the family avatar — never plan selection or create-avatar.
 * Applies to gift invitees and send-invite users (before and after payment).
 */
export function shouldRouteInviteeToChat({
  inviteId = null,
  iWillPay = null,
  userData = null,
} = {}) {
  return isKnownInviteeUser(userData, inviteId, iWillPay);
}

/**
 * If an invited user still needs to pick their relationship, return the relation-capture path.
 */
export async function resolveRelationCaptureRoute({
  avatarId = null,
  userId = null,
  avatars = [],
  avatarsAPI = null,
  userData = null,
  inviteId = null,
  iWillPay = null,
} = {}) {
  if (!avatarsAPI) return null;

  if (!shouldRouteInviteeToChat({ inviteId, iWillPay, userData })) {
    return null;
  }

  const n = await resolveInviteAvatarIdForRelationAsync({
    avatarId,
    userData,
    avatars,
    userId,
    avatarsAPI,
    inviteId,
    iWillPay,
  });
  if (!n) {
    return null;
  }

  if (userId && isRelationCaptured(userId, n)) {
    return null;
  }

  try {
    const relData = await avatarsAPI.getMemberRelation(n);
    const data = unwrapMemberRelationPayload(relData);
    const relation = data?.relation;
    if (relation != null && String(relation).trim() !== '') {
      if (userId) markRelationCaptured(userId, n);
      return null;
    }
    if (data?.needs_capture === true) {
      return `/relation-capture?avatar_id=${n}`;
    }
    if (data?.needs_capture === false) {
      const rel = relation != null ? String(relation).trim() : '';
      if (rel !== '' && rel !== 'unknown') {
        if (userId) markRelationCaptured(userId, n);
        return null;
      }
      // Server says no capture but relation is empty — still prompt invitee once.
      return `/relation-capture?avatar_id=${n}`;
    }
    // Invitee with a shared avatar but ambiguous API payload — still prompt once.
    return `/relation-capture?avatar_id=${n}`;
  } catch (_) {
    // e.g. race right after signup — relation screen will re-check on load.
    return `/relation-capture?avatar_id=${n}`;
  }
}

/** Route invited users to relation-capture (if needed) or chat; others use normal post-auth routing. */
export async function resolveInviteeOrPostAuthRoute({
  avatars = [],
  inviteId = null,
  iWillPay = null,
  userData = null,
  token = null,
  avatarId = null,
  userId = null,
  avatarsAPI = null,
  fromInviteLink = false,
} = {}) {
  // Invited users: payment first for unpaid send-invite, then relation capture or chat.
  if (shouldRouteInviteeToChat({ inviteId, iWillPay, userData })) {
    const inviteeRoute = await resolveInviteePostAuthRoute({
      avatars,
      inviteId,
      iWillPay,
      userData,
      userId,
      avatarId,
      avatarsAPI,
    });
    if (inviteeRoute) return inviteeRoute;
    return '/chat';
  }

  if (userHasAvatars(avatars)) {
    return '/chat';
  }

  return resolvePostAuthRoute({
    avatars,
    inviteId,
    iWillPay,
    userData,
    token,
    fromInviteLink,
  });
}

/**
 * Decide where to send a user immediately after signup/signin.
 * New users with an active trial but no avatar go to create-avatar.
 */
export function getPostAuthRoute({
  avatars = [],
  inviteId = null,
  iWillPay = null,
  userData = null,
  fromInviteLink = false,
} = {}) {
  if (userHasAvatars(avatars)) {
    return '/chat';
  }

  if (shouldRouteUnpaidSendInviteToPayment({ inviteId, iWillPay, userData })) {
    return '/select-plan';
  }

  if (shouldRouteInviteeToChat({ inviteId, iWillPay, userData })) {
    return '/chat';
  }

  if (hasActiveSubscription(userData)) {
    return '/create-avatar';
  }

  return '/select-plan';
}

/** Async route resolver — also checks /addons/my-plan when session user lacks subscription. */
export async function resolvePostAuthRoute({
  avatars = [],
  inviteId = null,
  iWillPay = null,
  userData = null,
  token = null,
  fromInviteLink = false,
} = {}) {
  if (userHasAvatars(avatars)) {
    return '/chat';
  }

  if (shouldRouteUnpaidSendInviteToPayment({ inviteId, iWillPay, userData })) {
    return '/select-plan';
  }

  if (shouldRouteInviteeToChat({ inviteId, iWillPay, userData })) {
    return '/chat';
  }

  if (hasActiveSubscription(userData)) {
    return '/create-avatar';
  }

  const apiSub = await fetchActiveSubscriptionFromApi(token || localStorage.getItem('access_token'));
  if (isSubscriptionActive(apiSub)) {
    return '/create-avatar';
  }

  return '/select-plan';
}

/** Where to send a user who already has an active plan/trial (e.g. from /select-plan). */
export async function resolveActivePlanRoute({ token, userId, avatarsAPI, userData = null } = {}) {
  try {
    const raw = sessionStorage.getItem('user_data');
    const parsed = raw ? JSON.parse(raw) : null;
    if (shouldRouteUnpaidSendInviteToPayment({ userData: userData || parsed })) {
      return '/select-plan';
    }
    if (shouldRouteInviteeToChat({ userData: userData || parsed })) {
      return '/chat';
    }
  } catch (_) { /* ignore */ }

  let avatars = getCachedAvatars();
  if (!userHasAvatars(avatars) && userId && avatarsAPI) {
    try {
      const res = await avatarsAPI.getAvatars(userId);
      avatars = res?.data?.avatars || res?.data || [];
      if (hasOwnedAvatars(avatars)) {
        try {
          sessionStorage.setItem('avatars_data', JSON.stringify(avatars));
          sessionStorage.setItem('has_avatars', 'true');
        } catch (_) { /* ignore */ }
      }
    } catch (_) {
      avatars = [];
    }
  }

  if (userHasAvatars(avatars)) {
    return '/chat';
  }

  return '/create-avatar';
}
